import { useState, type CSSProperties } from 'react';
import { daysFromToday } from '../lib/format';
import { localeDate } from '../lib/intl-cache';
import { useLocale } from '../i18n';
import type { Event } from '../types';

/** Category glyphs, in the page's line-icon style. */
export const Glyph = ({ category, className = 'size-6' }: { category: string; className?: string }) => {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true as const,
    className,
  };
  switch (category) {
    case 'Sports':
      return (
        <svg {...common}>
          <ellipse cx="12" cy="13" rx="9.5" ry="5.5" />
          <rect x="7.5" y="10.5" width="9" height="5" rx="1" />
          <path d="M2.5 13V9.5M21.5 13V9.5" />
        </svg>
      );
    case 'Comedy':
      return (
        <svg {...common}>
          <rect x="9" y="2.8" width="6" height="11.5" rx="3" />
          <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3.2M8.5 21.2h7" />
        </svg>
      );
    case 'Theater':
      return (
        <svg {...common}>
          <path d="M4 4.5h9v7a4.5 4.5 0 0 1-9 0Z" />
          <path d="M11 9.5h9v7a4.5 4.5 0 0 1-9 0v-7ZM6.2 7.7h.7M10 7.7h.7M6.6 9.9c.6.6 2 .6 2.6 0" />
          <path d="M13.5 12.7h.7M17.3 12.7h.7M14 15.7c.6-.6 2-.6 2.6 0" strokeWidth={1.4} />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <path d="M9 18V5l12-2v13" />
          <circle cx="6" cy="18" r="3" />
          <circle cx="18" cy="16" r="3" />
        </svg>
      );
  }
};

// Two hues per poster, picked from the category's family so a page of them
// reads as one set: music in violets and blues, comedy warm, theatre in wine
// and crimson, sports in blues and greens.
const PALETTES: Record<string, [number, number][]> = {
  Music: [[300, 262], [330, 282], [272, 222], [350, 302], [205, 255], [25, 325]],
  Comedy: [[58, 28], [38, 352], [72, 42], [22, 335]],
  Theater: [[18, 345], [352, 312], [8, 48], [330, 292]],
  Sports: [[252, 212], [158, 222], [265, 287], [198, 160], [232, 252]],
};

const hash = (s: string) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

/** The poster's colours: deterministic per performer, so the same act always looks the same. */
export const posterStyle = (name: string, category: string): CSSProperties => {
  const family = PALETTES[category] ?? PALETTES.Music;
  const h = hash(name || category);
  const [a, b] = family[h % family.length];
  const angle = 115 + (h % 60);
  return {
    backgroundImage: [
      `radial-gradient(85% 75% at 88% 12%, oklch(0.74 0.15 ${a} / 0.55), transparent 62%)`,
      `radial-gradient(70% 60% at 0% 100%, oklch(0.18 0.05 ${b} / 0.55), transparent 70%)`,
      `linear-gradient(${angle}deg, oklch(0.3 0.1 ${b}), oklch(0.5 0.16 ${a}))`,
    ].join(', '),
  };
};

export const initialsOf = (name: string) => {
  const words = (name || '?').replace(/^the\s+/i, '').split(/[\s\-–—/&+:]+/).filter((w) => /[\p{L}\p{N}]/u.test(w));
  // An acronym leads ("UFC 332", "WWE Raw", "NBA Finals") → the acronym itself.
  if (words[0] && /^[A-Z]{2,4}$/.test(words[0])) return words[0];
  // Otherwise the first letters of the first two real words: "#5 Ohio State at #14 Iowa" → "OS".
  return words
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter((w) => w && !/^\d+$/.test(w) && !/^(at|vs|v|and|of|with|feat|ft)$/i.test(w))
    .slice(0, 2)
    .map((w) => [...w][0])
    .join('')
    .toUpperCase();
};

/**
 * The designed stand-in for an event no source has a photo for: the act's
 * name on a gradient in its category's colours, with the category glyph as a
 * large watermark. Unique per act, never a stock photo of something else.
 * `hero` drops the name (the page title sits right beside it); `thumb` shows
 * initials.
 */
export const Poster = ({ name, category, variant = 'card', initials }: { name: string; category: string; variant?: 'card' | 'hero' | 'thumb' | 'glyph'; initials?: string }) => (
  <span aria-hidden className="absolute inset-0 overflow-hidden text-white" style={posterStyle(name, category)}>
    {variant === 'glyph' ? (
      <span className="flex size-full items-center justify-center">
        <Glyph category={category} className="size-[46%] opacity-60" />
      </span>
    ) : variant === 'thumb' ? (
      <span className="flex size-full items-center justify-center text-[15px] font-semibold tracking-[-0.02em]">{initials ?? initialsOf(name)}</span>
    ) : (
      <>
        <Glyph
          category={category}
          className={variant === 'hero' ? 'absolute -right-6 top-1/2 size-[300px] -translate-y-1/2 opacity-[0.14]' : 'absolute -right-4 -top-4 size-32 opacity-[0.16]'}
        />
        {variant === 'card' && (
          <span className="absolute bottom-3 left-3.5 right-12 line-clamp-2 text-[20px] font-semibold leading-[1.08] tracking-[-0.02em] [text-wrap:balance] [text-shadow:0_1px_10px_rgba(0,0,0,0.3)]">
            {name}
          </span>
        )}
      </>
    )}
  </span>
);

/** Performer photography frames the subject high; arena, stadium and team marks are centred. */
export const CROP: Record<string, string> = {
  Music: 'center 36%',
  Comedy: 'center 34%',
  Theater: 'center 38%',
  Sports: 'center 50%',
};

/** A smaller copy of the card photo where the source offers one: SeatGeek's CDN
 *  renders a 200×200 crop (7 KB instead of 30), Wikimedia standard thumb widths. */
const thumbOf = (url: string) =>
  url.includes('seatgeekimages.com/') ? url.replace(/\/(800x320|1200x525|huge)\.jpg$/, '/200x200.jpg') : url.replace(/\/960px-/, '/330px-');

/**
 * The picture that stands for an artist, a club or any list page: round, from the photo its
 * events carry (src/lib/hub-image.ts), else the generated poster with its initials — never an
 * empty circle. Decorative (the name always sits beside it).
 */
export const HubAvatar = ({ name, src, category = 'Music', size = 44, round = true, initials }: { name: string; src?: string | null; category?: string; size?: number; round?: boolean; initials?: string }) => {
  const [broken, setBroken] = useState(false);
  const url = !broken && src ? thumbOf(src) : null;
  return (
    <span className={`relative block shrink-0 overflow-hidden bg-muted ${round ? 'rounded-full' : 'rounded-lg'}`} style={{ width: size, height: size }} aria-hidden>
      {url ? (
        <img
          src={url}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          onError={() => setBroken(true)}
          className="size-full object-cover"
          style={{ objectPosition: CROP[category] ?? 'center 42%' }}
        />
      ) : (
        <Poster name={name} category={category} variant="thumb" initials={initials} />
      )}
    </span>
  );
};

/**
 * Square event thumbnail for rows and compact cards: the event's photo (or its
 * poster) with the date on a strip along the bottom — green when it's tonight.
 */
export const EventThumb = ({ event, size = 52, date = true, calm = false }: { event: Event; size?: number; date?: boolean; calm?: boolean }) => {
  const [broken, setBroken] = useState(false);
  const locale = useLocale();
  const name = event.artist ?? event.name;
  const src = !broken && event.imageUrl ? thumbOf(event.imageUrl) : null;
  const tonight = daysFromToday(event.date) === 0;
  const label = localeDate(new Date(event.date + 'T12:00:00'), locale, { month: 'short', day: 'numeric' }).replace(/\./g, '');
  return (
    <span className="relative block shrink-0 overflow-hidden rounded-lg bg-muted" style={{ width: size, height: size }}>
      {src ? (
        <img
          src={src}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          onError={() => setBroken(true)}
          className="size-full object-cover"
          style={{ objectPosition: CROP[event.category] ?? 'center 42%' }}
        />
      ) : (
        <Poster name={name} category={event.category} variant={calm ? 'glyph' : 'thumb'} />
      )}
      {date && (
        <span
          className={`tabular absolute inset-x-0 bottom-0 py-[3px] text-center text-[9.5px] font-semibold uppercase leading-none tracking-[0.03em] ${
            tonight ? 'bg-primary text-primary-foreground' : 'bg-black/65 text-white'
          }`}
        >
          {label}
        </span>
      )}
    </span>
  );
};
